<script setup>
  import { ref, shallowRef, markRaw, computed, useTemplateRef, watchEffect, watch, onMounted } from 'vue';
  // UMD side-effect import: registers window.jweather.UI.CanvasChart (rollup
  // does not interop CJS outside the project root).
  import '../../../UI/CanvasChart.js';

  const CanvasChart = window.jweather.UI.CanvasChart;
  import { useTidesStore } from "@/stores/TidesStore";
  import TideGraphNowMarker from "@/components/TideGraphNowMarker.vue";
  import { useMediaQuery } from '@/composables/useMediaQuery';
  import { useUserStore } from '@/stores/UserStore'

  const weekGraphContainer = useTemplateRef('week-graph-container');

  const graphPlotLeft = defineModel('plotLeftOffset', { type: Number, default: 0 });
  const graphPlotTop = defineModel('plotTopOffset', { type: Number, default: 0 });
  const graphPlotHeight = defineModel('plotHeight', { type: Number, default: 0 });

  const props = defineProps({
    startDate: {
      type: Date,
      required: true
    },
    endDate: {
      type: Date,
      required: true
    },
    containerId: {
      type: String,
      required: true
    },
    weekCount: {
      type: Number,
      default: 1
    },
    storeId: {
      type: Number,
      default: null
    }
  });

  const tideFillColor = {
    linearGradient: {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 1
    },
    stops: [
      [0, 'hsla(206, 94%, 87%, 1.0)'],
      [1, 'hsla(206, 94%, 87%, 0)']
    ]
  };

  const currentsFillColor = {
    symmetricAboutZero: true,
    linearGradient: {
      x1: 0,
      x2: 0,
      y1: 0,
      y2: 1
    },
    stops: [
      [0, 'hsla(286, 92%, 86%, 1.0)'],
      [0.5, 'hsla(286, 92%, 86%, 0)'],
      [1, 'hsla(286, 92%, 86%, 1.0)'],
    ]
  };

  const graphColorInfo = {
    tide: {
      color: 'hsla(211, 86%, 35%, 1.0)',
      fillColor: tideFillColor
    },
    currents: {
      color: 'hsla(287, 100%, 35%, 1.0)',
      fillColor: currentsFillColor
    }
  };

  const tidesStore = useTidesStore(props.storeId);
  const userStore = useUserStore();

  const isDesktop = useMediaQuery('(min-width: 840px)');

  // shallowRef + markRaw: a plain ref() deep-proxies the chart instance and
  // the data array handed to it, wiring chart-internal reads/writes into Vue
  // reactivity. Combined with the init effect below, that self-triggered into
  // a chart-rebuild-per-flush loop that pegged the main thread (LIF-181).
  const graph = shallowRef(null);
  const tideData = shallowRef(null);
  const tideDataMin = ref(null);
  const tideDataMax = ref(null);
  const nowTideValue = ref(null);
  const nowMarkerPosition = ref(null);
  const showGraph = ref(true);
  // Renderer elements, only touched inside drawNightBlocks — no reactivity.
  let nightBlocks = [];

  const tideSpotType = computed(() => tidesStore.selectedNearbyTideSpot.type);
  const lineColor = computed(() => graphColorInfo[tideSpotType.value]?.color);
  const fillColor = computed(() => graphColorInfo[tideSpotType.value]?.fillColor);
  const graphUnit = computed(() => tidesStore.selectedNearbyTideSpot.type === 'tide' ? userStore.units.height : 'kts');  
  const now = computed(() => Date.now());
  const solunarEvents = computed(() => tidesStore.getDateRangeSolunarEvents(props.startDate, props.endDate));

  const solunarEventGraphPoints = computed(() => {
    if (!graph.value || !solunarEvents.value || solunarEvents.value.length === 0) return [];

    return solunarEvents.value.map(event => {
      if (!event.eventName.toLowerCase().includes('sunset') && !event.eventName.toLowerCase().includes('sunrise') && !event.eventName.toLowerCase().includes('moonset') && !event.eventName.toLowerCase().includes('moonrise')) return null;
      
      const eventDateTime = event.dateTime;
      const iconName  = event.eventName.toLowerCase().replaceAll(',', '').replaceAll(' ', '-');
      const graphPoint = graph.value.series[0].points.find(point => {
        return point.x === eventDateTime
      });

      if (graphPoint) {
          graphPoint.tideEventName = event.eventName;
          graphPoint.tideEventValue = event.eventValue;
          graphPoint.tideEventIconName = iconName;
          return graphPoint;
      }
      
      return null;
    }).filter(point => point !== null);
  });  

  function initializeWeekGraph() {
    if (!weekGraphContainer.value) return;

    // The old chart (and its shapes) is discarded with the container's DOM.
    nightBlocks = [];

    graph.value = markRaw(new CanvasChart(weekGraphContainer.value, {
      chart: {
          type: 'areaspline',
          height: isDesktop.value ? '70px' : '80px',
          marginTop: isDesktop.value ? 20 : 30,
          marginRight: 0,
          marginLeft: 30,
          spacingBottom: 0,
          backgroundColor: 'transparent',
          events: {
            load: function () {
              // Fires during construction, before the markRaw'd assignment
              // below — mark here too so this early value is never proxied.
              graph.value = markRaw(this);
            },
            render: function () {
              graphPlotLeft.value = this.plotLeft;
              graphPlotHeight.value = this.plotHeight;
              graphPlotTop.value = this.plotTop;

              updateNowPosition();
              drawNightBlocks();
            }
          }          
        },
      credits: {
          enabled: false,
      },
      legend: {
          enabled: false,
      },      
      xAxis: {
          type: 'datetime',
          dateTimeLabelFormats: {
            day: '%l %p',
            hour: '%l %p',
            minute: '%l:%M %p',
            millisecond: '%l:%M %p',
          },
          gridLineWidth: 0,
          lineColor: 'hsl(0, 0%, 90%, 1)',
          labels: {
            enabled: false,
            useHTML: true,
            formatter: function () {
                const label = this.axis.defaultLabelFormatter.call(this);
                const isTimeLabel = label.indexOf('AM') > -1 || label.indexOf('PM') > -1;

                if (!isTimeLabel) {
                  return null;
                }

                const dayPeriod = label.indexOf('AM') > -1 ? 'A' : 'P';
                const time = label.split(' ')[0];

                return `<span class="jwx-graph__label-text">${time}${dayPeriod}</span>`;
            },
            step: 1,
          },
          minPadding: 0,
          maxPadding: 0,
          tickInterval: 3600 * 1000 * 6, // six hours
          tickLength: 0,
          showFirstLabel: false,
          endOnTick: false,
      },
      yAxis: {
        labels: {
          distance: 0,
          formatter: function () {
            return `<span class="jwx-tide-graph__y-axis-label">${this.value}</span>`;
          },
          padding: 0,
          overflow: 'allow',
          useHTML: true,
          x: -10,
        },
        max: tideDataMax.value && tideSpotType.value === 'currents' ? Math.ceil(tideDataMax.value) + 1 : null,
        min: tideDataMin.value && tideSpotType.value === 'currents' ? Math.floor(tideDataMin.value) - 1 : 0,
        maxPadding: tideSpotType.value === 'tide' ? .5 : null,
        showFirstLabel:  false,
        showLastLabel:  false,
        // Scale the interval to the data range so big-range spots (e.g. 26 ft
        // tides) don't cram a dozen labels into a ~70px-tall week row; cap at
        // roughly three ticks.
        tickInterval: (() => {
          const step = graphUnit.value === 'm' ? .5 : 1;
          const min = tideSpotType.value === 'currents' ? Math.floor(tideDataMin.value ?? 0) : 0;
          const range = Math.max((tideDataMax.value ?? 4) - min, step);

          return Math.max(step, Math.ceil(range / 3 / step) * step);
        })(),
        tickLength: 0,
        title: {
          enabled: false
        },
      },
      plotOptions: {
        series: {
          animation: false,
          states: {
            inactive: {
              opacity: 1
            }
          },
          point: {
            events: {
              mouseOver: () => false,
              mouseOut: () => false
            }
          }
        }
      },      
      series: [{
        name: 'Tide Data',
        data: tideData.value,
        color: lineColor.value,
        fillColor: fillColor.value,
        lineWidth: 4,
        pointStart: tideData.value?.length ? tideData.value[0].dateTime : null,
        zoneAxis: 'x',
        zones: [{
          value: now.value,
          color: 'hsla(0, 0%, 61%, 1.0)'
        }, {
          color: lineColor.value,
        }]          
      }],
      title: {
          text: null,
      },
      tooltip: {
          enabled: false,
      },
    }));
  }

  // Reads only: the interpolated "now" point is already in the series data
  // (insertNowPoint). Mutating the series here ran on every chart render,
  // piling up duplicate points and leaving the series permanently unsorted,
  // which made every subsequent paint re-sort the data.
  function updateNowPosition() {
    const nowPoint = graph.value.series[0].points.find(point => point.x === now.value);

    if (!nowPoint) {
      nowMarkerPosition.value = null;
      return;
    }

    const x = graph.value.xAxis[0].toPixels(now.value) - graph.value.plotLeft;
    const y = graph.value.series[0].yAxis.toPixels(nowPoint.y);

    nowMarkerPosition.value = { x, y };
  }

  // Splice the interpolated "now" point into the data once per data load, so
  // the line passes exactly through the now marker (same pattern as the
  // solunar event points below).
  function insertNowPoint() {
    const [interpolatedYValue, beforeIndex, afterIndex] = getInterpolatedYValue(now.value, tideData.value);

    if (isNaN(interpolatedYValue) || beforeIndex === null || afterIndex === null || beforeIndex === -1 || afterIndex === -1) return;

    nowTideValue.value = interpolatedYValue;
    tideData.value.splice(afterIndex, 0, [now.value, interpolatedYValue]);
  }

  function addSolunarEventGraphPoints() {
    if (!solunarEvents.value || solunarEvents.value.length === 0) return;

    solunarEvents.value.forEach(point => {
      const eventTime = point.dateTime;
      const [interpolatedYValue, beforeIndex, afterIndex] = getInterpolatedYValue(eventTime, tideData.value);

      if (isNaN(interpolatedYValue) || beforeIndex === null || afterIndex === null || beforeIndex === -1 || afterIndex === -1) return;

      const dataPoint = [eventTime, interpolatedYValue];
      tideData.value.splice(afterIndex, 0, dataPoint);
    });
  }

  function drawNightBlocks() {
    if (nightBlocks.length) {
      nightBlocks.forEach(block => block?.destroy());
      nightBlocks = [];
    }

    const sunEvents = solunarEventGraphPoints.value.filter(event => event.tideEventName.toLowerCase().includes('sun'));
    const y = 0;
    const height = graph.value.series[0].yAxis.height + graph.value.plotTop;
    const graphPoints = graph.value.series[0].points;
    const startXValue = graphPoints[0].x;
    const endXValue = graphPoints[graphPoints.length - 1].x;
    let x1 = graph.value.xAxis[0].toPixels(startXValue);
    let x2 = null;
    let width = 0;
    
    for (let i = 0; i < sunEvents.length; i++) {
      const event = sunEvents[i];

      if (event.tideEventName.toLowerCase().includes('sunrise')) {
        if (i === 0) {
          x1 = graph.value.xAxis[0].toPixels(startXValue);
          x2 = graph.value.xAxis[0].toPixels(event.x);
        } else {
          continue;
        }
      }

      if (event.tideEventName.toLowerCase().includes('sunset')) {
        if (i === sunEvents.length - 1) {
          x1 = graph.value.xAxis[0].toPixels(event.x);
          x2 = graph.value.xAxis[0].toPixels(endXValue);
        } else {
          x1 = graph.value.xAxis[0].toPixels(event.x);
          x2 = graph.value.xAxis[0].toPixels(sunEvents[i + 1].x);
        }
      }

      width = x2 - x1;

      const nightBlock = graph.value.renderer.rect(x1, y, width, height)
        .attr({
          fill: 'hsla(0, 0%, 90%, 0.4)',
          // Below the series (0) so the blocks shade behind the tide line,
          // not over it; grid lines paint before shapes so they stay under.
          zIndex: -1
        })
        .add();

      nightBlocks.push(nightBlock);
    }
  }

  async function updateTideData() {
    if (!tidesStore.selectedNearbyTideSpot) return;

    const responseData = await tidesStore.getAllTideDataByDateRange({
      tide_site_name: tidesStore.selectedNearbyTideSpot.location,
      begin_time: props.startDate,
      end_time: props.endDate,
      date_range_count: props.weekCount
    });

    const combinedData = [];
    Object.values(responseData.tides.data).forEach(dataSet => combinedData.push(...dataSet));

    if (!responseData || !responseData.tides || !responseData.tides.data || Object.keys(responseData.tides.data).length === 0) {
      showGraph.value = false;
      return;
    } else {
      showGraph.value = true;
    }

    const thinnedData = [];
    for (let i = 0; i < combinedData.length; i += 45) {
      const point = combinedData[i];

      thinnedData.push(point);
    }

    tideData.value = thinnedData;
    tideDataMin.value = responseData.tides.minValue;
    tideDataMax.value = responseData.tides.maxValue;

    addSolunarEventGraphPoints();
    insertNowPoint();
  }

  function getInterpolatedYValue(dateTime, dataSet) {
    let [beforeIndex, afterIndex] = getBeforeAndAfterDateTimeIndexes(dateTime, dataSet);

    if (beforeIndex === null || afterIndex === null || beforeIndex === -1 && afterIndex === -1) return [null, null, dataSet];

    const beforePointIndex = beforeIndex === -1 ? 0 : beforeIndex;
    const afterPointIndex = afterIndex === -1 ? dataSet.length - 1 : afterIndex;

    let beforePoint;
    let afterPoint;

    if (typeof dataSet[beforePointIndex] === 'object' && Object.prototype.hasOwnProperty.call(dataSet[beforePointIndex], 'x')) {
      beforePoint = dataSet[beforePointIndex]  ;
      afterPoint = dataSet[afterPointIndex];
    } else {
      beforePoint = { x: dataSet[beforePointIndex][0], y: dataSet[beforePointIndex][1] };
      afterPoint = { x: dataSet[afterPointIndex][0], y: dataSet[afterPointIndex][1] };
    };

    const nowBeforeTimeDelta = dateTime - beforePoint.x;
    const currentTimePeriodLength = afterPoint.x - beforePoint.x;
    const currentTimePeriodCompleteFactor = nowBeforeTimeDelta / currentTimePeriodLength;
    const interpolatedYValue = ((afterPoint.y - beforePoint.y) * currentTimePeriodCompleteFactor) + beforePoint.y;

    return [interpolatedYValue, beforeIndex, afterIndex];
  }    

  function getBeforeAndAfterDateTimeIndexes(dateTime, dataSet) {
    const dateProp = typeof dataSet[0] === 'object' && Object.prototype.hasOwnProperty.call(dataSet[0], 'x') ? 'x' : 0;
    const afterIndex = dataSet.findIndex(element => element[dateProp] > dateTime);
    let beforeIndex = null;

    for (let i = 0; i < dataSet.length; i++) {
      if (i === 0 && dateTime < dataSet[i][dateProp]) {
        beforeIndex = -1;
        break;
      }

      if (dataSet[i][dateProp] < dateTime) beforeIndex = i;
    }

    return [beforeIndex, afterIndex];
  }  

  async function reload() {
    if (graph.value) {
      graph.value.destroy();
      graph.value = null;
    }

    await updateTideData();

    initializeWeekGraph();
  }

  // Explicit sources on purpose: as a watchEffect this tracked everything the
  // init path read — including graph.value (via the solunarEventGraphPoints
  // computed in the render event) and, when tideData was a deep ref, the data
  // array addSolunarEventGraphPoints splices — while also writing both, so
  // every run re-triggered itself and rebuilt the chart per flush (LIF-181).
  // Solunar points are inserted by updateTideData before tideData is assigned,
  // so they're not (re-)added here.
  watch([weekGraphContainer, tideData], ([container, data]) => {
    if (!container || !data) return;

    initializeWeekGraph();
  }, { immediate: true });

  onMounted(async () => {
    watchEffect(async () => {
      if (!props.startDate || !props.endDate || !tidesStore.selectedNearbyTideSpot || !tidesStore.selectedNearbyTideSpot.location) return;
  
      await updateTideData();
    });
  });

  defineExpose({ reload });
</script>

<template>
  <div v-if="showGraph" ref="week-graph-container" :key="props.containerId" class="jwx-graph-calendar__week-graph"></div>
  <div v-if="showGraph" class="jwx-tide-graph__setup-block">
    <TideGraphNowMarker
      v-if="nowMarkerPosition"
      :position="nowMarkerPosition"
      :graph="graph"
    />
  </div>  
</template>